/**
 * Хранилище ключ-значение — единственное, что знают сторы о платформе.
 * Реализации: localStorage (сайт), Telegram CloudStorage, мост к SharedPreferences (Capacitor).
 */
export interface Storage {
  get(key: string): Promise<string | null>
  set(key: string, value: string): Promise<void>
  remove(key: string): Promise<void>
  /** Все известные ключи: нужны, чтобы собрать заметки и историю по месяцам. */
  keys(): Promise<string[]>
  /** Изменения извне: данные с часов в Capacitor, другая вкладка в вебе. Возвращает отписку. */
  onChange?(callback: (key: string) => void): () => void
}

/**
 * Telegram CloudStorage не принимает значения длиннее 4096 байт, поэтому длинные
 * значения режутся на части: `notes_2026_09`, `notes_2026_09~1`, `notes_2026_09~2`.
 * Механизм общий для всех платформ, чтобы формат хранения нигде не расходился.
 */
const MAX_VALUE_BYTES = 4000
const CHUNK_SEPARATOR = '~'

const encoder = new TextEncoder()

function byteLength(text: string): number {
  return encoder.encode(text).length
}

/** Режет строку на части, каждая не длиннее лимита в байтах; суррогатные пары не разрываются. */
export function splitIntoChunks(value: string, maxBytes = MAX_VALUE_BYTES): string[] {
  if (byteLength(value) <= maxBytes) return [value]
  const chunks: string[] = []
  let current = ''
  let currentBytes = 0
  for (const char of value) {
    const size = byteLength(char)
    if (currentBytes + size > maxBytes) {
      chunks.push(current)
      current = ''
      currentBytes = 0
    }
    current += char
    currentBytes += size
  }
  if (current.length > 0) chunks.push(current)
  return chunks
}

function chunkKey(key: string, index: number): string {
  return index === 0 ? key : `${key}${CHUNK_SEPARATOR}${index}`
}

/** Читает значение целиком, склеивая части. */
export async function getLong(storage: Storage, key: string): Promise<string | null> {
  const first = await storage.get(key)
  if (first === null || first === undefined) return null
  let result = first
  for (let i = 1; ; i++) {
    const part = await storage.get(chunkKey(key, i))
    if (part === null || part === undefined) break
    result += part
  }
  return result
}

/** Пишет значение, разрезая на части и убирая хвост от прошлой, более длинной записи. */
export async function setLong(storage: Storage, key: string, value: string): Promise<void> {
  const chunks = splitIntoChunks(value)
  for (let i = 0; i < chunks.length; i++) await storage.set(chunkKey(key, i), chunks[i])
  for (let i = chunks.length; ; i++) {
    const tail = await storage.get(chunkKey(key, i))
    if (tail === null || tail === undefined) break
    await storage.remove(chunkKey(key, i))
  }
}

export async function removeLong(storage: Storage, key: string): Promise<void> {
  await storage.remove(key)
  for (let i = 1; ; i++) {
    const tail = await storage.get(chunkKey(key, i))
    if (tail === null || tail === undefined) break
    await storage.remove(chunkKey(key, i))
  }
}

/** Логические ключи без частей: `notes_2026_09~1` схлопывается в `notes_2026_09`. */
export function logicalKeys(keys: string[]): string[] {
  const set = new Set(keys.map((k) => k.split(CHUNK_SEPARATOR)[0]))
  return [...set]
}

/** Хранилище в памяти — для тестов и для окружения без постоянного хранилища. */
export class MemoryStorage implements Storage {
  private readonly map = new Map<string, string>()
  private readonly listeners = new Set<(key: string) => void>()

  async get(key: string): Promise<string | null> {
    return this.map.has(key) ? (this.map.get(key) as string) : null
  }

  async set(key: string, value: string): Promise<void> {
    this.map.set(key, value)
    this.listeners.forEach((l) => l(key))
  }

  async remove(key: string): Promise<void> {
    this.map.delete(key)
    this.listeners.forEach((l) => l(key))
  }

  async keys(): Promise<string[]> {
    return [...this.map.keys()]
  }

  onChange(callback: (key: string) => void): () => void {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }

  /** Размер значения в байтах — тестам удобно проверять лимит Telegram. */
  bytesOf(key: string): number {
    const value = this.map.get(key)
    return value === undefined ? 0 : byteLength(value)
  }
}

/** Хранилище сайта: localStorage плюс события из других вкладок. */
export class LocalStorage implements Storage {
  constructor(private readonly prefix = 'moonwake.') {}

  private full(key: string): string {
    return this.prefix + key
  }

  async get(key: string): Promise<string | null> {
    return globalThis.localStorage.getItem(this.full(key))
  }

  async set(key: string, value: string): Promise<void> {
    globalThis.localStorage.setItem(this.full(key), value)
  }

  async remove(key: string): Promise<void> {
    globalThis.localStorage.removeItem(this.full(key))
  }

  async keys(): Promise<string[]> {
    const result: string[] = []
    for (let i = 0; i < globalThis.localStorage.length; i++) {
      const key = globalThis.localStorage.key(i)
      if (key !== null && key.startsWith(this.prefix)) result.push(key.slice(this.prefix.length))
    }
    return result
  }

  onChange(callback: (key: string) => void): () => void {
    const handler = (event: StorageEvent) => {
      if (event.key !== null && event.key.startsWith(this.prefix)) callback(event.key.slice(this.prefix.length))
    }
    globalThis.addEventListener('storage', handler)
    return () => globalThis.removeEventListener('storage', handler)
  }
}
